import React from 'react';
import clsx from 'clsx';
import { t } from 'i18next';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { Button, OverviewHeader } from '@brighterion/br-ui';
import { handleKeyEvent, handleOnClickEvent } from '@brighterion/utils/general';
import { FormikFieldString } from 'Components/FormikComponents';
import RuleGroupConditions from 'Components/RuleGroupConditions';
import PageHeader from 'Components/PageHeader';
import ButtonGroup from 'Components/ButtonGroup';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { RuleGroupConditionsSchema, ruleGroupConditionsSchema } from './ruleGroupConditionsSchema';

import styles from './RuleGroupConditionsForm.css';

const RuleGroupConditionsForm = () => {
    const { addAlert } = useAlertBanner();
    const history = useHistory();

    const handleCancel = () => {
        history.push('/global-rules');
    };

    const initialValues: RuleGroupConditionsSchema = {
        name: '',
        executionOrder: 1,
        enabled: true,
        conditions: [
            {
                parameter: '',
                operator: '',
                values: '',
                readonly: false
            }
        ]
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={ruleGroupConditionsSchema}
            onSubmit={(_values, formikHelpers) => {
                /** Demo purpose only */
                history.push('/global-rules');

                const ruleGroupId = 42;
                addAlert({
                    id: 'create-rule-group-conditions-success',
                    content: t('ruleGroupConditions.form.notifications.success', { ruleGroupId }),
                    status: 'success'
                });

                formikHelpers.setSubmitting(false);
            }}
        >
            {(formikProps) => {
                const {
                    dirty,
                    errors,
                    isValid,
                    touched,
                    isSubmitting,
                    handleChange,
                    handleBlur,
                    handleSubmit
                } = formikProps;
                const disableSave = !dirty || !isValid;
                return (
                    <>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>New Rule Group</PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                <ButtonGroup direction='row'>
                                    <Button
                                        dataTestId='rule-group-conditions-form-cancel-button'
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyEvent(handleCancel)}
                                        onClick={handleOnClickEvent(handleCancel)}
                                        size='medium'
                                        variant='tertiary'
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        dataTestId='rule-group-conditions-form-save-button'
                                        disabled={disableSave || isSubmitting}
                                        onKeyDown={handleKeyEvent(() => handleSubmit())}
                                        onClick={handleOnClickEvent(() => handleSubmit())}
                                        size='small'
                                        variant='primary'
                                    >
                                        {isSubmitting ? t('Saving...') : t('Save Rule')}
                                    </Button>
                                </ButtonGroup>
                            </PageHeader.Right>
                        </PageHeader>
                        <OverviewHeader
                            containerClassName={styles.overviewHeader}
                            dataTestId='Rule-Group-Conditions-Overview-Header'
                            leftTitle={t('ruleGroupConditions.form.overviewHeaders.information')}
                            rightTitle={undefined}
                        >
                            <div className={clsx([styles.formGroup, styles.information])}>
                                <FormikFieldString
                                    label={t('ruleGroupConditions.form.name.label')}
                                    optionalMsg={t('ruleGroupConditions.form.name.helpText')}
                                    type='text'
                                    name='name'
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isSubmitting}
                                    error={!!(errors.name && touched.name)}
                                    errorMsg={errors.name?.toString()}
                                />
                                <FormikFieldString
                                    label={t('ruleGroupConditions.form.executionOrder.label')}
                                    type='number'
                                    name='executionOrder'
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    min={1}
                                    disabled={isSubmitting}
                                    error={!!(errors.executionOrder && touched.executionOrder)}
                                    errorMsg={errors.executionOrder?.toString()}
                                />
                            </div>
                        </OverviewHeader>
                        <OverviewHeader
                            containerClassName={styles.overviewHeader}
                            dataTestId='Rule-Group-Conditions-Overview-Header'
                            leftTitle={t('ruleGroupConditions.form.overviewHeaders.condition')}
                            rightTitle={undefined}
                        >
                            <RuleGroupConditions />
                        </OverviewHeader>
                    </>
                );
            }}
        </Formik>
    );
};

export default RuleGroupConditionsForm;
